import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { createWebPageSchema, createBreadcrumbSchema, createOrganizationSchema } from '@/components/feature/SEO';
import WhatsAppButton from '@/components/base/WhatsAppButton';

const FORM_URL = 'https://readdy.ai/api/form/d75lmmlr5g02gnd69j2g';

const PartnershipPage = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://unqcode.com';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    revenue: '',
    partnershipType: '',
    budget: '',
    website: '',
    message: '',
    goals: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', industry: '', revenue: '', partnershipType: '', budget: '', website: '', message: '', goals: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const benefits = isAr
    ? [
        { icon: 'ri-money-dollar-circle-line', title: 'لا مخاطرة مسبقة', desc: 'لا تدفع رسوماً ضخمة. نستثمر في نموك ونكسب عندما تكسب.' },
        { icon: 'ri-bar-chart-grouped-line', title: 'حوافز متوافقة', desc: 'فريقنا محفّز 100٪ لتقديم نتائج لأننا نشارك في نجاحك.' },
        { icon: 'ri-shield-star-line', title: 'خبرة مُثبتة', desc: 'أكثر من 120 عميل على هذا النموذج في 15+ قطاعاً.' },
        { icon: 'ri-customer-service-2-line', title: 'فريق متكامل', desc: 'تحصل على فريق مخصص من المسوقين والمصممين والمحللين.' },
      ]
    : [
        { icon: 'ri-money-dollar-circle-line', title: 'Zero Upfront Risk', desc: "You don't pay large retainers. We invest in your growth and earn when you earn." },
        { icon: 'ri-bar-chart-grouped-line', title: 'Aligned Incentives', desc: 'Our team is 100% motivated to deliver because we share in your success.' },
        { icon: 'ri-shield-star-line', title: 'Proven Expertise', desc: '120+ clients on this model across 15+ industries with consistent results.' },
        { icon: 'ri-customer-service-2-line', title: 'Full Service Team', desc: 'You get a dedicated team of marketers, designers, and analysts.' },
      ];

  const industries = isAr
    ? ['التجارة الإلكترونية', 'العقارات', 'البرمجيات كخدمة (SaaS)', 'الرعاية الصحية', 'التعليم', 'الضيافة والسياحة', 'التمويل والتقنية المالية', 'البيع بالتجزئة', 'التصنيع', 'أخرى']
    : ['E-commerce', 'Real Estate', 'SaaS', 'Healthcare', 'Education', 'Hospitality', 'FinTech', 'Retail', 'Manufacturing', 'Other'];

  const revenues = isAr
    ? ['أقل من 100 ألف ريال', '100 ألف – 500 ألف', '500 ألف – 2 مليون', '2 مليون – 10 مليون', 'أكثر من 10 مليون']
    : ['Under $30K', '$30K – $150K', '$150K – $500K', '$500K – $2M', 'Over $2M'];

  const partnershipTypes = isAr
    ? ['تقاسم الأرباح', 'تسويق رقمي', 'بناء موقع أو تطبيق', 'استراتيجية تقنية', 'شراكة شاملة']
    : ['Revenue Sharing', 'Digital Marketing', 'Website / App Build', 'Tech Strategy', 'Full Partnership'];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={isAr ? t('seo.partnershipTitle') : t('seo.partnershipTitle')}
        description={isAr ? t('seo.partnershipDesc') : t('seo.partnershipDesc')}
        keywords="partnership program, strategic partnership, revenue sharing, business partnership Saudi Arabia, digital agency partner, برنامج الشراكة, شراكة استراتيجية"
        canonical={`${siteUrl}/partnership`}
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            createWebPageSchema(
              isAr ? 'طلب الشراكة - برنامج الشراكة الاستراتيجية | الكود المميز' : 'Partnership Application - Strategic Partnership Program | Unique Code',
              isAr
                ? 'تقدم بطلب للشراكة مع الكود المميز. نموذج تقاسم الأرباح بدون استثمار مسبق.'
                : 'Apply for partnership with Unique Code. Revenue sharing model with zero upfront investment.',
              '/partnership'
            ),
            createOrganizationSchema(),
            createBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: isAr ? 'الشراكة' : 'Partnership', url: '/partnership' },
            ]),
            {
              '@type': 'Service',
              '@id': `${siteUrl}/partnership#service`,
              name: isAr ? 'برنامج الشراكة الاستراتيجية' : 'Strategic Partnership Program',
              description: isAr
                ? 'نموذج تقاسم الأرباح مع الكود المميز — نستثمر في تسويقك ونكسب عندما تكسب. بدون رسوم مسبقة ضخمة.'
                : 'Revenue sharing partnership model with Unique Code — we invest in your marketing and earn when you earn. No large upfront retainers.',
              serviceType: 'Revenue Sharing Partnership',
              provider: {
                '@id': `${siteUrl}/#organization`,
              },
              areaServed: [
                { '@type': 'Country', name: 'Saudi Arabia' },
                { '@type': 'Country', name: 'United Arab Emirates' },
                { '@type': 'AdministrativeArea', name: 'GCC' },
              ],
              offers: {
                '@type': 'Offer',
                description: isAr
                  ? 'نموذج تقاسم الأرباح — لا رسوم مسبقة ضخمة، ندفع معاً عند النجاح'
                  : 'Revenue sharing model — no large upfront fees, we earn together on success',
                priceCurrency: 'SAR',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  description: 'Performance-based revenue share (15%–30% of generated revenue)',
                },
              },
              hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: isAr ? 'خدمات الشراكة' : 'Partnership Services',
                itemListElement: [
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Revenue Sharing' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital Marketing' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Website & App Build' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Tech Strategy' } },
                ],
              },
            },
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b0a1326d1ff40ab8d2ad5914b8f9e210.jpg"
            alt="Partnership"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/90 to-gray-900/60"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-brand-red/20 border border-brand-red/40 rounded-full px-4 py-2 mb-7">
              <i className="ri-shake-hands-line text-brand-red text-sm"></i>
              <span className="text-white/80 text-sm font-medium">
                {isAr ? 'نموذج الشراكة الاستراتيجية' : 'Strategic Partnership Program'}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-5">
              {isAr ? (
                <>
                  كن <span className="text-brand-red">شريكنا</span>
                  <span className="block text-white/60 text-2xl md:text-3xl font-light mt-2">وننمو معاً</span>
                </>
              ) : (
                <>
                  Become Our <span className="text-brand-red">Partner</span>
                  <span className="block text-white/60 text-2xl md:text-3xl font-light mt-2">and grow together</span>
                </>
              )}
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
              {isAr
                ? 'تقاسم الأرباح يعني أننا نستثمر في تسويقك إلى جانبك. إذا لم تنمُ، لم نكسب. أكمل النموذج أدناه وسنتواصل معك خلال 48 ساعة.'
                : "Revenue sharing means we invest in your marketing alongside you. If you don't grow, we don't earn. Fill the form below and we'll contact you within 48 hours."}
            </p>
            <div className="flex flex-wrap gap-4">
              {[
                { icon: 'ri-time-line', text: isAr ? 'رد خلال 48 ساعة' : 'Response in 48h' },
                { icon: 'ri-shield-check-line', text: isAr ? 'بيانات آمنة ومحمية' : 'Secure & Confidential' },
                { icon: 'ri-money-dollar-circle-line', text: isAr ? 'استشارة مجانية' : 'Free Consultation' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-white/60 text-sm">
                  <i className={`${item.icon} text-brand-red`}></i>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
      </section>

      {/* Benefits strip */}
      <section className="py-16 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-brand-red/30 transition-all group">
                <div className="w-11 h-11 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red rounded-xl mb-4 transition-colors duration-300">
                  <i className={`${b.icon} text-brand-red group-hover:text-white text-lg transition-colors duration-300`}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Form Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16 items-start">
            {/* Left Info */}
            <div className="lg:col-span-2">
              <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">
                {isAr ? 'انضم للبرنامج' : 'Join the Program'}
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-5">
                {isAr ? 'نموذج طلب الشراكة' : 'Partnership Application'}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                {isAr
                  ? 'شاركنا تفاصيل عملك وسيقوم فريقنا بتقييم مدى تطابق نموذج تقاسم الأرباح مع نشاطك التجاري، ثم نتواصل معك لمناقشة الخطوات التالية.'
                  : "Share your business details and our team will evaluate if our revenue-sharing model is a good fit. We'll then reach out to discuss next steps."}
              </p>

              <div className="space-y-5">
                {[
                  { step: '01', title: isAr ? 'أكمل النموذج' : 'Fill the Form', desc: isAr ? 'أجب على الأسئلة بصدق لنفهم وضعك الحالي.' : 'Answer questions honestly so we understand your situation.' },
                  { step: '02', title: isAr ? 'مراجعة الطلب' : 'Application Review', desc: isAr ? 'يراجع فريقنا طلبك خلال 48 ساعة.' : 'Our team reviews your application within 48 hours.' },
                  { step: '03', title: isAr ? 'مكالمة استراتيجية' : 'Strategy Call', desc: isAr ? 'نحجز مكالمة مجانية لمناقشة التفاصيل والفرص.' : 'We schedule a free call to discuss details and opportunities.' },
                  { step: '04', title: isAr ? 'إطلاق الشراكة' : 'Launch Partnership', desc: isAr ? 'نوقع الاتفاقية ونبدأ العمل معاً فوراً.' : 'Sign the agreement and start working together immediately.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-brand-red rounded-xl text-white text-xs font-black flex-shrink-0 mt-0.5">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm mb-0.5">{item.title}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-brand-red/5 border border-brand-red/20 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-brand-red text-lg mt-0.5 flex-shrink-0"></i>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {isAr
                      ? 'جميع المعلومات المشاركة تُعامَل بسرية تامة. لن نشارك بياناتك مع أي طرف ثالث.'
                      : 'All information shared is treated with complete confidentiality. We never share your data with third parties.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {status === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-3xl p-12 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-green-600 text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-3">
                    {isAr ? 'تم استلام طلبك!' : 'Application Received!'}
                  </h3>
                  <p className="text-gray-500 mb-6 leading-relaxed">
                    {isAr
                      ? 'شكراً لاهتمامك بالشراكة مع الكود المميز. سيتواصل معك فريقنا خلال 48 ساعة.'
                      : 'Thank you for your interest in partnering with Unique Code. Our team will contact you within 48 hours.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => setStatus('idle')}
                      className="px-6 py-3 bg-brand-red text-white font-bold rounded-xl hover:bg-brand-red-dark transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {isAr ? 'إرسال طلب آخر' : 'Submit Another'}
                    </button>
                    <Link to="/">
                      <button className="px-6 py-3 border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                        {isAr ? 'العودة للرئيسية' : 'Back to Home'}
                      </button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="partnership-application"
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl border border-gray-100 p-8 space-y-6"
                >
                  {/* Row 1 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'الاسم الكامل' : 'Full Name'} <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={isAr ? 'اسمك الكامل' : 'Your full name'}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'البريد الإلكتروني' : 'Email Address'} <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'رقم الجوال' : 'Phone Number'} <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+966 5XX XXX XXXX"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'اسم الشركة' : 'Company Name'} <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={isAr ? 'اسم شركتك أو نشاطك التجاري' : 'Your company or business name'}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'القطاع التجاري' : 'Business Industry'} <span className="text-brand-red">*</span>
                      </label>
                      <select
                        name="industry"
                        required
                        value={formData.industry}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors cursor-pointer bg-white"
                      >
                        <option value="">{isAr ? 'اختر القطاع' : 'Select industry'}</option>
                        {industries.map((ind) => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'نوع الشراكة المطلوبة' : 'Partnership Type'} <span className="text-brand-red">*</span>
                      </label>
                      <select
                        name="partnershipType"
                        required
                        value={formData.partnershipType}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors cursor-pointer bg-white"
                      >
                        <option value="">{isAr ? 'اختر نوع الشراكة' : 'Select type'}</option>
                        {partnershipTypes.map((pt) => (
                          <option key={pt} value={pt}>{pt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'الإيرادات الشهرية الحالية' : 'Current Monthly Revenue'}
                      </label>
                      <select
                        name="revenue"
                        value={formData.revenue}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors cursor-pointer bg-white"
                      >
                        <option value="">{isAr ? 'اختر النطاق' : 'Select range'}</option>
                        {revenues.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAr ? 'الموقع الإلكتروني (اختياري)' : 'Website URL (optional)'}
                      </label>
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        placeholder="https://yourwebsite.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Goals */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {isAr ? 'أهدافك من الشراكة' : 'Partnership Goals'} <span className="text-brand-red">*</span>
                    </label>
                    <input
                      type="text"
                      name="goals"
                      required
                      value={formData.goals}
                      onChange={handleChange}
                      placeholder={isAr ? 'مثال: زيادة المبيعات 50٪، توسيع السوق، رفع الوعي بالعلامة...' : 'e.g. Increase sales 50%, expand market, raise brand awareness...'}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {isAr ? 'أخبرنا عن عملك وتحدياته' : 'Tell Us About Your Business'} <span className="text-brand-red">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={isAr ? 'صف عملك، تحدياتك الحالية، ولماذا تعتقد أن نموذج تقاسم الأرباح مناسب لك...' : 'Describe your business, current challenges, and why you think revenue sharing works for you...'}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400 resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${formData.message.length > 450 ? 'text-red-500' : 'text-gray-400'}`}>
                      {formData.message.length}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-500"></i>
                      <p className="text-red-700 text-sm">{isAr ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'Something went wrong. Please try again.'}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full py-4 bg-brand-red text-white font-black text-base rounded-xl hover:bg-brand-red-dark transition-all hover:scale-[1.02] disabled:opacity-60 disabled:scale-100 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {isAr ? 'جاري الإرسال...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        {isAr ? 'إرسال طلب الشراكة' : 'Submit Partnership Application'}
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    {isAr ? 'بإرسال هذا النموذج، أنت توافق على' : 'By submitting, you agree to our'}{' '}
                    <Link to="/privacy" className="text-brand-red hover:underline">{isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '+120', label: isAr ? 'شريك نشط' : 'Active Partners' },
              { value: '3.8x', label: isAr ? 'متوسط نمو الإيرادات' : 'Avg Revenue Growth' },
              { value: '97%', label: isAr ? 'معدل الرضا' : 'Satisfaction Rate' },
              { value: '+15', label: isAr ? 'قطاع تجاري' : 'Business Sectors' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl font-black text-white mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default PartnershipPage;
